import React, { useState, useRef, useEffect } from "react";
import { Typography, TextField, Button, CircularProgress } from "@mui/material";
import { signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";
import { auth } from "../../firebase";

const PhoneInput = ({
  phoneNumber,
  setPhoneNumber,
  loading,
  setLoading,
  setShowOtpInput,
  setConfirmationResult,
  setSuccessMessage,
  setErrorMessage,
  handleBackToLogin,
}) => {
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const mountedRef = useRef(true);
  const recaptchaVerifierRef = useRef(null);

  // Validate phone number
  useEffect(() => {
    const digitCount = phoneNumber.replace(/\D/g, "").length;
    const isValid = digitCount >= 10 && phoneNumber.startsWith("+");
    setIsPhoneValid(isValid);
  }, [phoneNumber]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const validatePhoneForm = () => {
    let isValid = true;
    setPhoneNumberError("");
    const digitCount = phoneNumber.replace(/\D/g, "").length;
    if (!phoneNumber || digitCount < 10) {
      setPhoneNumberError("Valid phone number is required with at least 10 digits");
      isValid = false;
    }
    if (isValid && !phoneNumber.startsWith("+")) {
      setPhoneNumberError("Phone number must include country code (e.g., +1 for US)");
      isValid = false;
    }
    return isValid;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formattedValue = value.startsWith("+") ? value : `+${value}`;
    setPhoneNumber(formattedValue);
    setPhoneNumberError("");
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validatePhoneForm() || loading || !mountedRef.current) return;

    setLoading(true);
    try {
      const formattedPhone = phoneNumber.trim().replace(/[\s()-]/g, "");
      //console.log("Initiating OTP send to:", formattedPhone);

      // Initialize invisible reCAPTCHA
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {
          //console.log("reCAPTCHA verified successfully");
        },
        "expired-callback": () => {
          //console.warn("reCAPTCHA expired");
          setErrorMessage("Security verification expired. Please try again.");
        },
      });

      await recaptchaVerifierRef.current.render();
      //console.log("reCAPTCHA rendered");

      const confirmationResultObj = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        recaptchaVerifierRef.current
      );
      if (!mountedRef.current) return;

      setConfirmationResult(confirmationResultObj);
      setShowOtpInput(true);
      setSuccessMessage(`OTP sent to ${formattedPhone}`);
    } catch (error) {
      //console.error("Error sending OTP:", error);
      let errorMsg = "Failed to send OTP. Please try again.";
      if (error.code === "auth/invalid-phone-number") {
        errorMsg = "The phone number is invalid. Please check the format (e.g., +12025550123).";
      } else if (error.code === "auth/captcha-check-failed") {
        errorMsg = "Security verification failed. Please try again or disable ad blockers.";
      } else if (error.code === "auth/quota-exceeded") {
        errorMsg = "SMS quota exceeded. Please try again later.";
      } else if (error.code === "auth/too-many-requests") {
        errorMsg = "Too many attempts. Please try again later.";
      } else if (error.code === "auth/network-request-failed") {
        errorMsg = "Network error. Please check your connection and try again.";
      }
      setErrorMessage(errorMsg);
    } finally {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <form onSubmit={handleSendOtp} style={{ padding: "1rem" }}>
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <h2 style={{ fontSize: "1.25rem", fontWeight: "500", marginBottom: "0.25rem" }}>
          Continue with OTP
        </h2>
        <p style={{ fontSize: "0.875rem", color: "#666", marginTop: "0.25rem" }}>
          We'll send you a one-time password (OTP) to verify your phone number
        </p>
      </div>
      <div style={{ marginTop: "1.5rem" }}>
        <Typography variant="h6">Phone Number</Typography>
        <TextField
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder="Enter phone number (e.g., +12025550123)"
          fullWidth
          error={!!phoneNumberError}
          helperText={phoneNumberError || "Include country code with + (e.g., +1 for US)"}
          InputProps={{
            sx: {
              borderRadius: "16px",
              border: "2px solid rgba(24, 48, 89, 1)",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              fontSize: "16px",
              height: "56px",
            },
          }}
        />
      </div>
      <div id="recaptcha-container" />
      <Button
        type="submit"
        disabled={!isPhoneValid || loading}
        variant="contained"
        style={{
          width: "100%",
          borderRadius: "16px",
          marginTop: "20px",
          backgroundColor: isPhoneValid ? "#FFFF00" : "#E0E0E0",
          color: isPhoneValid ? "#000000" : "#999999",
          boxShadow: "none",
          fontWeight: "600",
          fontSize: "24px",
          textTransform: "none",
          height: "56px",
          position: "relative",
        }}
      >
        {loading ? (
          <CircularProgress size={28} sx={{ color: "#000000", position: "absolute" }} />
        ) : (
          "Send OTP"
        )}
      </Button>
      <div style={{ marginTop: "20px", textAlign: "center" }}>
        <Button
          onClick={handleBackToLogin}
          style={{ color: "#20A8E0", textTransform: "none", fontSize: "16px" }}
        >
          Back to Login
        </Button>
      </div>
    </form>
  );
};

export default PhoneInput;